import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";
import { getAnalyticsValue } from "../util/helpers";

const createPDF = (options = {}) => {
  const doc = new jsPDF(options);
  doc.setR2L(true);
  return doc;
};

const setHeader = (doc, headerFunction, headerRecord) => {
  if (headerFunction) {
    headerFunction(doc, headerRecord);
  }
};

const setContent = (doc, contentFunction, contentRecord) => {
  if (contentFunction) {
    contentFunction(doc, contentRecord);
  }
};

const createTable = (doc, tableData, intl, category, options = {}) => {
  const table = {
    ...getAutoTableData(tableData, intl, category, options.reverseText),
    didParseCell: (data) => {
      const isHebrew = (text) => /[\u0590-\u05FF]/.test(text);
      if (!isHebrew(data.cell.text[0])) {
        data.cell.text = data.cell.text[0].split("").reverse().join("");
      }
    },
    ...options,
    styles: {
      ...options.styles,
      font: "Rubik",
      halign: "right",
      overflow: "linebreak",
    },
    headStyles: {
      ...options.headStyles,
      fillColor: [200, 200, 200], // Light gray background
      textColor: [0, 0, 0], // Black text
      fontStyle: "bold",
    },
  };
  doc.autoTable(table);
};

const setFooter = (doc, footerFunction) => {
  if (footerFunction && footerFunction !== "paging") {
    footerFunction(doc);
  } else if (footerFunction === "paging") {
    addPagingFooter(doc);
  }
};

const addPagingFooter = (doc) => {
  const pageCount = doc.internal.getNumberOfPages();
  doc.setFontSize(10);
  doc.setFont("Rubik", "normal");
  const pageHeight = doc.internal.pageSize.height;
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.text(`עמוד ${i} מתוך ${pageCount}`, 20, pageHeight - 10, {
      align: "center",
    });
  }
};

const getAutoTableData = (data, intl, category, reverseText = false) => {
  let columns = [];
  if (data[0]) {
    let keys = Object.keys(data[0]);
    if (reverseText) {
      keys = keys.reverse();
    }

    keys.forEach((key) =>
      columns.push({
        header: intl.formatMessage({
          id:
            category === "analytics"
              ? `app.analytics.${
                  key.split(".").length - 1 > 1
                    ? key.substr(0, key.lastIndexOf("."))
                    : key
                }`
              : `app.print.${category}.${key}`,
          defaultMessage: `${key}`,
        }),
        dataKey: key,
      })
    );
  }

  const translated = data.map((item) => {
    const formattedItem = {};
    let keys = Object.keys(item);
    if (reverseText) {
      keys = keys.reverse();
    }

    keys.forEach((key) => {
      formattedItem[key] = getAnalyticsValue(intl, item[key]);
    });
    return formattedItem;
  });

  return {
    columns: columns,
    body: translated,
    headStyles: {
      fillColor: "#464646",
    },
    alternateRowStyles: {
      fillColor: "#FAFAFA",
    },
  };
};

const exportPDF = (doc, fileName, action = "download") => {
  const timestamp = moment().format("HH:mm DD/MM/YYYY");
  if (action === "print") {
    window.open(doc.output("bloburl"), "_blank");
  } else {
    doc.save(`${fileName}-${timestamp}.pdf`);
  }
};

export const exportReportToPdf = (fileName, tableData, options = {}) => {
  const {
    setHeader: headerFunction,
    headerRecord,
    setTopContent,
    setBottomContent,
    bottomContentRecord,
    setFooter: footerFunction,
    intl,
    category,
    tableOptions = {},
    orientation,
    action,
    reverseText = false,
  } = options;

  const doc = createPDF({ orientation });

  if (setTopContent) {
    setContent(doc, setTopContent);
  }

  setHeader(doc, headerFunction, headerRecord);

  createTable(doc, tableData, intl, category, {
    ...tableOptions,
    reverseText,
  });

  if (setBottomContent) {
    setContent(doc, setBottomContent, bottomContentRecord);
  }

  setFooter(doc, footerFunction);
  exportPDF(doc, fileName, action);
};

export default exportReportToPdf;
